import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TenantAdminSummary, TenantStatus } from '@parking/shared';
import { api } from '../shared/api/client';
import { enumLabel } from '../shared/i18n/enumLabel';
import { PageHeader } from '../shared/ui/PageHeader';
import { Card } from '../shared/ui/Card';
import { ParkingDataTable } from '../shared/ui/ParkingDataTable';
import { AppModal } from '../shared/ui/AppModal';
import { StatusBadge, statusToneFromEnum } from '../shared/ui/StatusBadge';

type CreateForm = {
  slug: string;
  name: string;
  adminEmail: string;
  adminPassword: string;
  adminFirstName: string;
  adminLastName: string;
};

const emptyCreate: CreateForm = {
  slug: '',
  name: '',
  adminEmail: '',
  adminPassword: '',
  adminFirstName: 'Admin',
  adminLastName: 'Tenant',
};

export function PlatformTenantsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [edit, setEdit] = useState<TenantAdminSummary | null>(null);
  const [form, setForm] = useState<CreateForm>(emptyCreate);
  const [editName, setEditName] = useState('');
  const [editStatus, setEditStatus] = useState<TenantStatus>('ACTIVE');
  const [error, setError] = useState<string | null>(null);

  const tenants = useQuery({
    queryKey: ['platform-tenants'],
    queryFn: () => api.platformListTenants(),
  });

  const createMut = useMutation({
    mutationFn: () =>
      api.platformCreateTenant({
        slug: form.slug.trim(),
        name: form.name.trim(),
        adminEmail: form.adminEmail.trim() || undefined,
        adminPassword: form.adminPassword || undefined,
        adminFirstName: form.adminFirstName.trim() || undefined,
        adminLastName: form.adminLastName.trim() || undefined,
      }),
    onSuccess: async () => {
      setCreateOpen(false);
      setForm(emptyCreate);
      setError(null);
      await qc.invalidateQueries({ queryKey: ['platform-tenants'] });
      await qc.invalidateQueries({ queryKey: ['platform-dashboard'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const updateMut = useMutation({
    mutationFn: () =>
      api.platformUpdateTenant(edit!.id, { name: editName.trim(), status: editStatus }),
    onSuccess: async () => {
      setEdit(null);
      setError(null);
      await qc.invalidateQueries({ queryKey: ['platform-tenants'] });
      await qc.invalidateQueries({ queryKey: ['platform-dashboard'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const rows = tenants.data?.data ?? [];
  const columns = [
    t('platform.tenants.slug'),
    t('platform.tenants.name'),
    t('platform.tenants.status'),
    t('platform.tenants.users'),
    t('platform.tenants.parkings'),
    t('platform.tenants.sessions'),
    t('common.actions'),
  ];

  return (
    <>
      <PageHeader
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setError(null);
              setForm(emptyCreate);
              setCreateOpen(true);
            }}
          >
            {t('platform.tenants.create')}
          </button>
        }
      />

      {tenants.isError && (
        <div className="alert alert-danger">{(tenants.error as Error).message}</div>
      )}

      <Card>
        <ParkingDataTable
          storageKey="platform-tenants"
          columns={columns}
          refreshKey={rows.map((ten) => `${ten.id}:${ten.status}`).join('|')}
          galleryItems={rows.map((ten) => ({
            id: ten.id,
            icon: 'bi-buildings',
            iconTone: statusToneFromEnum(ten.status),
            title: ten.slug,
            subtitle: ten.name,
            meta: `${ten.usersCount} users · ${ten.parkingsCount} parkings · ${ten.openSessions} sessions`,
            badge: (
              <StatusBadge tone={statusToneFromEnum(ten.status)}>
                {enumLabel(t, 'tenantStatus', ten.status)}
              </StatusBadge>
            ),
            actions: (
              <button
                type="button"
                className="btn btn-sm btn-icon btn-ghost-primary"
                onClick={() => {
                  setEdit(ten);
                  setEditName(ten.name);
                  setEditStatus(ten.status);
                  setError(null);
                }}
              >
                <i className="bi bi-pencil" />
              </button>
            ),
          }))}
        >
          {rows.map((ten) => (
            <tr key={ten.id}>
              <td className="fw-semibold">{ten.slug}</td>
              <td>{ten.name}</td>
              <td>
                <StatusBadge tone={statusToneFromEnum(ten.status)}>
                  {enumLabel(t, 'tenantStatus', ten.status)}
                </StatusBadge>
              </td>
              <td>{ten.usersCount}</td>
              <td>{ten.parkingsCount}</td>
              <td>{ten.openSessions}</td>
              <td>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-ghost-primary"
                  onClick={() => {
                    setEdit(ten);
                    setEditName(ten.name);
                    setEditStatus(ten.status);
                    setError(null);
                  }}
                >
                  <i className="bi bi-pencil" />
                </button>
              </td>
            </tr>
          ))}
        </ParkingDataTable>
      </Card>

      <AppModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t('platform.tenants.create')}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={() => setCreateOpen(false)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={createMut.isPending}
              onClick={() => createMut.mutate()}
            >
              {t('common.save')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          {error && <div className="alert alert-danger mb-0">{error}</div>}
          <div>
            <label className="form-label">{t('platform.tenants.slug')}</label>
            <input
              className="form-control"
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              required
            />
            <div className="form-text">{t('platform.tenants.slugHint')}</div>
          </div>
          <div>
            <label className="form-label">{t('platform.tenants.name')}</label>
            <input
              className="form-control"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
            />
          </div>
          <div className="fw-semibold">{t('platform.tenants.adminSection')}</div>
          <div>
            <label className="form-label">{t('platform.tenants.adminEmail')}</label>
            <input
              className="form-control"
              value={form.adminEmail}
              onChange={(e) => setForm((f) => ({ ...f, adminEmail: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">{t('platform.tenants.adminPassword')}</label>
            <input
              className="form-control"
              type="password"
              value={form.adminPassword}
              onChange={(e) => setForm((f) => ({ ...f, adminPassword: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">{t('platform.tenants.adminFirstName')}</label>
            <input
              className="form-control"
              value={form.adminFirstName}
              onChange={(e) => setForm((f) => ({ ...f, adminFirstName: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">{t('platform.tenants.adminLastName')}</label>
            <input
              className="form-control"
              value={form.adminLastName}
              onChange={(e) => setForm((f) => ({ ...f, adminLastName: e.target.value }))}
            />
          </div>
        </div>
      </AppModal>

      <AppModal
        open={Boolean(edit)}
        onClose={() => setEdit(null)}
        title={t('platform.tenants.edit')}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={() => setEdit(null)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={updateMut.isPending}
              onClick={() => updateMut.mutate()}
            >
              {t('common.save')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          {error && <div className="alert alert-danger mb-0">{error}</div>}
          <div>
            <label className="form-label">{t('platform.tenants.name')}</label>
            <input
              className="form-control"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
          </div>
          <div>
            <label className="form-label">{t('platform.tenants.status')}</label>
            <select
              className="form-select"
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value as TenantStatus)}
            >
              {['ACTIVE', 'SUSPENDED', 'PENDING'].map((s) => (
                <option key={s} value={s}>
                  {enumLabel(t, 'tenantStatus', s)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </AppModal>
    </>
  );
}
